import type { EventoDetalle } from '../../api/tipos';
import { Avatar } from '../../componentes/Avatar';
import { EsqueletoPanelConfirmados } from '../../componentes/Esqueletos';
import { Vacio } from '../../componentes/Estados';
import { Resultados } from '../../componentes/Resultados';
import { lineaDeEvento } from '../../lib/fechas';
import { COLOR_POR_DEFECTO } from '../../lib/personas';

interface Props {
  detalle: EventoDetalle | undefined;
  cargando: boolean;
  onResponder: () => void;
}

export function PanelConfirmados({ detalle, cargando, onResponder }: Props) {
  if (cargando && !detalle) return <EsqueletoPanelConfirmados />;

  if (!detalle) {
    return (
      <section className="tarjeta overflow-hidden">
        <div className="h-1 bg-white/10" />
        <Vacio titulo="Elegí un evento de la lista para ver quiénes van." />
      </section>
    );
  }

  const color = detalle.tipo?.color ?? COLOR_POR_DEFECTO;
  const yaRespondio = detalle.miEstado !== 'SIN_RESPONDER';

  return (
    <section className="tarjeta overflow-hidden">
      <div className="h-1" style={{ background: color }} />

      <div className="flex flex-col gap-4 px-5 pt-5 sm:flex-row sm:items-start sm:gap-5 sm:px-6 sm:pt-6">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            {detalle.tipo && (
              <span
                className="rounded-[5px] px-2 py-[3px] text-[10.5px] font-semibold uppercase tracking-[.7px] text-tinta"
                style={{ background: color }}
              >
                {detalle.tipo.nombre}
              </span>
            )}
            <span className="text-[11px] uppercase tracking-[1.3px] text-debil">
              Lista de confirmados
            </span>
          </div>

          <h2 className="mt-2 font-titulo text-[24px] font-extrabold leading-tight sm:text-[26px]">
            {detalle.titulo}
          </h2>

          <p className="mt-1.5 text-[13px] text-tenue">
            {lineaDeEvento(detalle.fechaLocal, detalle.horaLocal, detalle.lugar)}
          </p>
        </div>

        <button
          type="button"
          onClick={onResponder}
          disabled={detalle.cerrado && !yaRespondio}
          className={`flex-none rounded-[10px] px-5 py-2.5 font-titulo text-[13.5px] font-bold transition-colors ${
            yaRespondio || detalle.cerrado
              ? 'border border-white/15 bg-white/[.07] text-titulo hover:bg-white/10 disabled:opacity-50'
              : 'bg-lima text-tinta hover:bg-lima-claro'
          }`}
        >
          {detalle.cerrado
            ? 'Ver el evento'
            : yaRespondio
              ? 'Cambiar mi respuesta'
              : 'Responder asistencia'}
        </button>
      </div>

      <div className="px-5 pb-5 pt-5 sm:px-6">
        <div className="inline-flex items-baseline gap-3 rounded-[11px] border border-white/[.08] bg-white/[.04] px-5 py-3">
          <span className="font-titulo text-[30px] font-extrabold leading-none text-lima">
            {detalle.confirmados}
          </span>
          <span className="text-[11px] uppercase tracking-[1.1px] text-debil">
            {detalle.confirmados === 1 ? 'confirmado' : 'confirmados'}
          </span>
        </div>
      </div>

      {/* El backend manda los resultados sin las preguntas privadas. */}
      {detalle.resultados.length > 0 && (
        <>
          <div className="h-px bg-white/[.08]" />
          <div className="px-5 py-5 sm:px-6 sm:py-6">
            <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[1.3px] text-debil">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-lima" aria-hidden />
              Votación en vivo
            </div>
            <Resultados resultados={detalle.resultados} compacto />
          </div>
        </>
      )}

      <div className="h-px bg-white/[.08]" />

      <div className="px-5 py-5 sm:px-6 sm:py-6">
        <div className="mb-3 text-[11px] uppercase tracking-[1.3px] text-debil">Quiénes van</div>

        {detalle.confirmadas.length === 0 ? (
          <p className="text-[12.5px] text-mudo">
            Todavía no confirmó nadie. Podés ser el primero.
          </p>
        ) : (
          <div className="grid max-h-[420px] grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2 overflow-y-auto pr-1">
            {detalle.confirmadas.map((persona) => (
              <div
                key={persona.id}
                className="flex items-center gap-2.5 rounded-[10px] border border-white/[.06] bg-white/[.04] px-2.5 py-1.5"
              >
                <Avatar usuarioId={persona.id} nombre={persona.nombre} tamano={30} />
                <span className="min-w-0 truncate text-[13px] text-titulo">{persona.nombre}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
